import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  cancelSopGeneration,
  createSop,
  deleteSop,
  fetchSop,
  fetchSops,
  fetchVideos,
  regenerateSop,
  updateSop,
} from '../../request/sop-synthesizer/sopSynthesizer.api.ts';
import { sopKeys } from '../../queries/sop-synthesizer/keys.ts';
import { getApiErrorMessage } from '../../request/getApiErrorMessage.ts';

export const useSopVideos = (enabled = true) =>
  useQuery({
    queryKey: sopKeys.videos(),
    queryFn: fetchVideos,
    enabled,
    // Keep the picker current while an administrator is preparing a video.
    refetchInterval: (q) => (q.state.data?.some((v) => v.media_status === 'processing') ? 5000 : false),
  });

export const useSops = () =>
  useQuery({
    queryKey: sopKeys.list(),
    queryFn: fetchSops,
    // Poll while any SOP is still being generated.
    refetchInterval: (q) => (q.state.data?.some((s) => s.status === 'generating') ? 4000 : false),
  });

export const useSop = (id: string | undefined) =>
  useQuery({
    queryKey: sopKeys.detail(id ?? ''),
    queryFn: () => fetchSop(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.sop.status === 'generating' ? 3000 : false),
  });

export const useCreateSop = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSop,
    onSuccess: () => qc.invalidateQueries({ queryKey: sopKeys.list() }),
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not start the SOP')),
  });
};

export const useRegenerateSop = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instructions?: string) => regenerateSop(id, instructions),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.detail(id) });
      qc.invalidateQueries({ queryKey: sopKeys.list() });
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not start generation')),
  });
};

export const useDeleteSop = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSop,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.list() });
      toast.success('SOP deleted');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not delete the SOP')),
  });
};

export const useUpdateSop = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title?: string; description?: string }) => updateSop(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.detail(id) });
      qc.invalidateQueries({ queryKey: sopKeys.list() });
      toast.success('Details saved');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not save the details')),
  });
};

export const useCancelSopGeneration = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cancelSopGeneration(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.detail(id) });
      qc.invalidateQueries({ queryKey: sopKeys.list() });
      toast.success('Generation cancelled');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not cancel')),
  });
};
